<template>
  <div v-clickoutside="() => setOpen(false)" class="lp-select">
    <span
      v-if="labelTop || labelTopSlotExist"
      class="lp-input-label font-weight-medium ly-row ly-spacebetween-center"
    >
      <slot name="label-top" :props="props">
        <span>
          <span>{{ labelTop }}</span>
          <span v-if="required" class="text-blue">*</span>
        </span>
      </slot>
    </span>

    <div
      class="lp-select-inner"
      :class="[
        message
          ? `lp-input-wrapper-danger`
          : status && `lp-input-wrapper-${status}`,
        disabled && `lp-input-wrapper-disabled`,
      ]"
      :style="message ? '' : 'margin-bottom: 22px;'"
    >
      <div
        ref="trigger"
        :tabindex="disabled ? -1 : 0"
        class="lp-select-selection form-faux-select"
        role="listbox"
        :class="selectionClasses"
        :aria-label="placeholder"
        @click="toggleDropdown"
        @keydown.tab="() => setOpen(false)"
        @keydown.down.stop.prevent="handleKey(EDirections.DOWN)"
        @keydown.up.stop.prevent="handleKey(EDirections.UP)"
        @keydown.esc.stop.prevent="() => setOpen(false)"
        @keydown.enter.stop.prevent="handleEnterKey"
      >
        <span
          :class="
            isOptionsGroup ? 'lp-select-selected' : 'lp-select-placeholder'
          "
        >
          <slot name="placeholder" :props="props">
            {{ placeholder }}
          </slot>
          <slot name="selection" :props="props" />
        </span>

        <slot name="icon" :props="props">
          <lp-icon
            name="chevron-down"
            :class="show && 'lp-select-arrow--active'"
          />
        </slot>
      </div>
      <transition name="drop-bottom">
        <div v-show="show" class="lp-select-dropdown" :class="dropdownClasses">
          <div
            :ref="(dropdown) => setSelectListRef(dropdown)"
            class="lp-select-list menu-pop-up"
          >
            <div :class="search && 'lp-select-search-wrapper'">
              <lp-input
                v-if="search"
                v-model="searchField"
                :placeholder="searchPlaceholder"
                prefix-icon="search"
                prefix-icon-aria-hidden="true"
                size="compact"
                @keyup="searchEmit"
              >
                <template #clear>
                  <div v-show="clearable && searchField" class="clearable">
                    <slot name="clear" :on-clear="clearModelValue">
                      <lp-icon
                        class="lp-input-icon-wrapper"
                        name="close"
                        role="button"
                        @click="clearModelValue()"
                      />
                    </slot>
                  </div>
                </template>
              </lp-input>
            </div>
            <div v-if="$slots.default" :class="search && 'search-active'">
              <slot :show="show" />
            </div>
          </div>
        </div>
      </transition>
    </div>
    <transition name="fade-bottom">
      <div class="ly-row">
        <span
          v-if="message"
          class="lp-input-message mt-half d-flex"
          :class="[
            message
              ? `lp-input-message-danger`
              : status && `lp-input-message-${status}`,
            disabled && `lp-input-message-disabled`,
          ]"
        >
          {{ message }}
        </span>
        <span v-else class="lp-input-message mt-half text-gray">
          {{ helper }}
        </span>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, watch, ref, provide } from 'vue'
import { Positions } from '@/models/enums'
import { clickOutside } from '@/directives'
import { useSelect } from './hooks'
import { TEmit } from '../../types'
import { TProps } from '@/types/global'
import { ALLOWED_POSITION } from './constants'
import { TSelectProps } from './types'
import { EDirections } from '@/models/enums'
import { useCheckSlot } from '@/hooks'
import uuid from '@/helpers/uuid'
import { t } from '@/plugins/i18n'

export default defineComponent({
  name: 'lp-base-select',
  computed: {
    t() {
      return t
    },
  },
  directives: {
    clickoutside: clickOutside,
  },
  props: {
    id: {
      type: String,
      default: uuid('select'),
    },
    placement: {
      type: String,
      default: Positions.B,
      validator: (value: Positions) => ALLOWED_POSITION.includes(value),
    },
    disabled: { type: Boolean, default: false },
    divided: { type: Boolean, default: false },
    labelTop: { type: String, default: null },
    search: { type: Boolean, default: true },
    isOptionsGroup: { type: Boolean, default: null },
    placeholder: { type: String, default: t('Shared.SelectOption') },
    required: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    status: { type: String, default: 'neutral' },
    message: { type: String, default: null },
    helper: { type: String, default: null },
    searchPlaceholder: { type: String, default: t('Shared.FilterOptions') },
  },
  emits: ['search-emit', 'update:modelValue'],
  setup(props: TProps, { emit, slots }) {
    const labelTopSlotExist = useCheckSlot(slots, 'label-top') !== null
    const searchField = ref(null)
    const disabled = ref(props.disabled)
    const {
      show,
      handleEnterKey,
      setOpen,
      setSelectListRef,
      toggleDropdown,
      handleKey,
    } = useSelect(props as TSelectProps, emit as TEmit)

    const toggleShow = (option) => {
      show.value = !show.value
      emit('update:modelValue', option, true)
    }

    provide('toggleShow', toggleShow)
    const selectionClasses = computed(() => ({
      'lp-select-selection-disabled': props.disabled,
      'lp-select-selection-active': show.value,
    }))

    const dropdownClasses = computed(() => ({
      [props.placement
        ? `lp-select-dropdown-${props.placement}`
        : `lp-select-dropdown-${Positions.B}`]: true,
      'lp-select-dropdown-divided': props.divided,
    }))

    const searchEmit = () => {
      emit('search-emit', searchField.value)
    }

    const clearModelValue = () => {
      searchField.value = undefined
      searchEmit()
    }

    watch(
      () => show.value,
      () => {
        if (!show.value) {
          searchField.value = undefined
          searchEmit()
        }
      },
      { immediate: true },
    )

    return {
      labelTopSlotExist,
      show,
      handleEnterKey,
      setOpen,
      setSelectListRef,
      toggleDropdown,
      handleKey,
      selectionClasses,
      dropdownClasses,
      props,
      EDirections,
      searchField,
      searchEmit,
      disabled,
      clearModelValue,
    }
  },
})
</script>
